import { FileUploadType } from '@bemlo/enums'
import { fileUploadTypesForMarket } from '@bemlo/utils'

import { FileUpload } from './FileUpload'
import { Button, Div, Flex } from '../../../elements'
import { useTranslations } from '../../../translate'
import { SectionHeading } from '../components/SectionHeading'
import { useCandidateView } from '../hooks/useCandidateView'
import { CandidateViewCandidate, CandidateViewDocument } from '../types'

const { CV, HOSP, IVO, REFERENCE, OTHER, SPECIALIZATION } = FileUploadType

/**
 * Creates the cards in which file uploads are displayed or can be added.
 */
const useFileUploadCards = (candidate: CandidateViewCandidate) => {
  const { actor, market } = useCandidateView()
  const { fileUploads: uploads = [] } = candidate
  const slots = []

  if (actor.can('update', 'fileUploads')) {
    const referenceFileUploads = uploads.filter(
      (f) => f.uploadType === REFERENCE,
    )
    const allowedFileUploadTypes = fileUploadTypesForMarket(market)
    slots.push(
      ...[
        {
          key: 'cv-1',
          type: CV,
          fileUpload: uploads.find((f) => f.uploadType === CV),
        },
        {
          key: 'ivo-1',
          type: IVO,
          fileUpload: uploads.find((f) => f.uploadType === IVO),
        },
        {
          key: 'hosp-1',
          type: HOSP,
          fileUpload: uploads.find((f) => f.uploadType === HOSP),
        },
        {
          key: 'reference-1',
          type: REFERENCE,
          fileUpload: referenceFileUploads[0],
        },
        {
          key: 'reference-2',
          type: REFERENCE,
          fileUpload: referenceFileUploads[1],
        },
        {
          key: 'specialization',
          type: SPECIALIZATION,
          fileUpload: uploads.find((f) => f.uploadType === SPECIALIZATION),
        },
      ].filter((upload) => allowedFileUploadTypes.includes(upload.type)),
    )
  }

  for (const fileUpload of uploads) {
    if (!slots.some((slot) => slot.fileUpload?.id === fileUpload.id)) {
      slots.push({
        type: fileUpload.uploadType,
        fileUpload,
        key: fileUpload.id,
      })
    }
  }

  slots.sort((a, b) => (b.fileUpload ? 1 : 0) - (a.fileUpload ? 1 : 0))

  return slots
}

export type FileUploadsProps = {
  onAddFileUploadClick: (uploadType: FileUploadType) => void
  onEditFileUploadClick: (fileUpload: CandidateViewDocument) => void
  onOpenFileUploadClick: (fileUpload: CandidateViewDocument) => void
}

export const FileUploads = ({
  onAddFileUploadClick,
  onEditFileUploadClick,
  onOpenFileUploadClick,
}: FileUploadsProps) => {
  const { actor, data } = useCandidateView()
  const fileUploadCards = useFileUploadCards(data)
  const t = useTranslations()

  if (!fileUploadCards.length || !actor.can('read', 'fileUploads')) {
    return null
  }

  return (
    <Flex col>
      <SectionHeading>
        {t.candidate_view.file_uploads.documents()}
      </SectionHeading>
      <Flex col gap={3} items="start">
        <Div
          styles={{
            display: 'grid',
            gap: 3.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))',
            w: 'full',
          }}
        >
          {fileUploadCards.map(({ fileUpload, type, key }) => (
            <FileUpload
              fileUpload={fileUpload ?? null}
              key={fileUpload?.id ?? key}
              onAddClick={onAddFileUploadClick}
              onEditClick={onEditFileUploadClick}
              onOpenClick={onOpenFileUploadClick}
              uploadType={type}
            />
          ))}
        </Div>
        {actor.can('update', 'fileUploads') && (
          <Button
            design="primary-inverted"
            onClick={() => onAddFileUploadClick(OTHER)}
            size="sm"
          >
            {t.candidate_view.file_uploads.upload_document()}
          </Button>
        )}
      </Flex>
    </Flex>
  )
}
